import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import logger from '#/utils/logger';
import { DEFAULT_BRANDING } from './defaults';
import {
  AvatarOutline,
  BrandingManifest,
  MachineBrandingOverride,
  ResolvedBranding,
} from './types';

const MANIFEST_URL = '/branding/manifest.json';

interface BrandingContextType {
  branding: ResolvedBranding;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

function resolveOutline(
  override: Partial<AvatarOutline> | undefined,
  fallback: AvatarOutline
): AvatarOutline {
  if (!override) return fallback;
  return {
    color: override.color ?? '#ffffff',
    width: override.width ?? 2,
  };
}

function globToRegExp(pattern: string): RegExp {
  const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
  return new RegExp(`^${escaped}$`, 'i');
}

function findManifestEntry(
  manifest: BrandingManifest,
  machine: string
): MachineBrandingOverride | undefined {
  if (!machine) return undefined;
  const machineLower = machine.toLowerCase();
  // 1. Direct key match
  const directKey = Object.keys(manifest).find((k) => k.toLowerCase() === machineLower);
  if (directKey) return manifest[directKey];
  // 2. Exact match in machines[] array
  for (const key in manifest) {
    const entry = manifest[key];
    if (entry?.machines?.some((m) => m.toLowerCase() === machineLower)) return entry;
  }
  // 3. Glob pattern match via machinesPattern
  for (const key in manifest) {
    const entry = manifest[key];
    const patterns = entry?.machinesPattern;
    if (!patterns) continue;
    const list = Array.isArray(patterns) ? patterns : [patterns];
    if (list.some((p) => globToRegExp(p).test(machine))) return entry;
  }
  return undefined;
}

function resolveBranding(override?: MachineBrandingOverride): ResolvedBranding {
  return {
    colors: { ...DEFAULT_BRANDING.colors, ...(override?.colors ?? {}) },
    logo: override?.logo ?? DEFAULT_BRANDING.logo,
    avatar: override?.avatar ?? DEFAULT_BRANDING.avatar,
    avatarOutline: resolveOutline(override?.avatarOutline, DEFAULT_BRANDING.avatarOutline),
    aiMessageOutline: resolveOutline(override?.aiMessageOutline, DEFAULT_BRANDING.aiMessageOutline),
    userMessageOutline: resolveOutline(override?.userMessageOutline, DEFAULT_BRANDING.userMessageOutline),
    promptOutline: resolveOutline(override?.promptOutline, DEFAULT_BRANDING.promptOutline),
    imageOutline: resolveOutline(override?.imageOutline, DEFAULT_BRANDING.imageOutline),
    playbackPillOutline: resolveOutline(override?.playbackPillOutline, DEFAULT_BRANDING.playbackPillOutline),
    checklistTriggerOutline: resolveOutline(override?.checklistTriggerOutline, DEFAULT_BRANDING.checklistTriggerOutline),
    topBarCenter: override?.topBarCenter,
    heroImage: override?.heroImage,
    promptPlaceholder: override?.promptPlaceholder,
    poweredByLogo: override?.poweredByLogo ?? DEFAULT_BRANDING.poweredByLogo,
    translations: { ...DEFAULT_BRANDING.translations, ...(override?.translations ?? {}) },
  };
}

function applyBrandingVars(branding: ResolvedBranding) {
  const root = document.documentElement;
  root.style.setProperty('--button-bg', branding.colors.primary);
  root.style.setProperty('--button-border', branding.colors.accent);
  root.style.setProperty('--button-border-from', branding.colors.gradientFrom);
  root.style.setProperty('--button-border-to', branding.colors.gradientTo);
  root.style.setProperty('--color-bg', branding.colors.background);
  root.style.setProperty('--message-color', branding.colors.messageBackground);
  root.style.setProperty('--color-text', branding.colors.text);
  root.style.setProperty('--ai-message-text', branding.colors.aiMessageText);
  root.style.setProperty('--color-user-message', branding.colors.userMessageBackground);
  root.style.setProperty('--color-user-message-text', branding.colors.userMessageText);
  root.style.setProperty('--prompt-bg', branding.colors.promptBackground);
  root.style.setProperty('--prompt-text', branding.colors.promptText);
  root.style.setProperty('--suggestion-bg', branding.colors.suggestionBackground);
  root.style.setProperty('--suggestion-text', branding.colors.suggestionText);
  root.style.setProperty('--pause-button-bg', branding.colors.pauseButton);
  root.style.setProperty('--pause-icon-color', branding.colors.pauseIcon);
  root.style.setProperty('--gear-icon-color', branding.colors.gearIcon);
  root.style.setProperty('--microphone-icon-color', branding.colors.microphoneIcon);
  root.style.setProperty('--feedback-icon-color', branding.colors.feedbackIcon);
  root.style.setProperty('--feedback-dialog-bg', branding.colors.feedbackDialogBg);
  root.style.setProperty('--feedback-dialog-border', branding.colors.feedbackDialogBorder);
  root.style.setProperty('--feedback-dialog-title-text', branding.colors.feedbackDialogTitleText);
  root.style.setProperty('--feedback-dialog-text', branding.colors.feedbackDialogText);
  root.style.setProperty('--feedback-dialog-button-bg', branding.colors.feedbackDialogButtonBg);
  root.style.setProperty('--feedback-dialog-input-bg', branding.colors.feedbackDialogInputBg);
  root.style.setProperty('--feedback-dialog-back-bg', branding.colors.feedbackDialogBackBg);
  root.style.setProperty('--feedback-dialog-close-bg', branding.colors.feedbackDialogCloseBg);
  root.style.setProperty('--timestamp-text', branding.colors.timestampText);
  root.style.setProperty('--visit-link-text', branding.colors.visitLinkText);
  root.style.setProperty('--spinner-dot', branding.colors.spinnerDot);
  root.style.setProperty('--spinner-track', branding.colors.spinnerTrack);
  root.style.setProperty('--loader-pill-bg', branding.colors.loaderPillBg);
  root.style.setProperty('--page-bg', branding.colors.pageBg);
  root.style.setProperty('--trigger-icon-color', branding.colors.triggerIconColor);
  root.style.setProperty('--checklist-icon-color', branding.colors.checklistIconColor);
  root.style.setProperty('--checklist-border', branding.colors.checklistBorder);
  root.style.setProperty('--checklist-progress-track', branding.colors.checklistProgressTrack);
  root.style.setProperty('--checklist-checkbox-border', branding.colors.checklistCheckboxBorder);
  root.style.setProperty('--checklist-checkmark', branding.colors.checklistCheckmark);
  root.style.setProperty('--checklist-button-bg', branding.colors.checklistButtonBg);
  root.style.setProperty('--checklist-button-text', branding.colors.checklistButtonText);
  root.style.setProperty('--checklist-done-bg', branding.colors.checklistDoneBg);
  root.style.setProperty('--checklist-header-text', branding.colors.checklistHeaderText);
  root.style.setProperty('--checklist-trigger-bg', branding.colors.checklistTriggerBg);
  root.style.setProperty('--checklist-trigger-text', branding.colors.checklistTriggerText);
  root.style.setProperty('--signout-icon-color', branding.colors.signoutIcon);
  root.style.setProperty('--hero-image-max-width', branding.heroImage?.maxWidth ?? '500px');
  root.style.setProperty('--hero-image-max-height', branding.heroImage?.maxHeight ?? '500px');
  root.style.setProperty('--hero-image-border-radius', branding.heroImage?.borderRadius ?? '0');
  root.style.setProperty('--image-arrow-bg', branding.colors.imageArrowBackground);
  root.style.setProperty('--image-arrow-text', branding.colors.imageArrowText);
  root.style.setProperty('--metadata-bubble-text', branding.colors.metadataBubbleText);
  root.style.setProperty('--metadata-bubble-subtle-bg', branding.colors.metadataBubbleSubtleBg);
  root.style.setProperty('--checklist-header-bg', branding.colors.checklistHeaderBg);
  root.style.setProperty('--checklist-text', branding.colors.checklistText);
  root.style.setProperty('--language-label-text', branding.colors.languageLabelText);
  root.style.setProperty('--settings-modal-bg', branding.colors.settingsModalBg);
  root.style.setProperty('--settings-text', branding.colors.settingsText);
  root.style.setProperty('--settings-inactive-tab-text', branding.colors.settingsInactiveTabText);
  root.style.setProperty('--top-bar-border', branding.colors.topBarBorder);
  root.style.setProperty('--top-bar-bg', branding.colors.topBarBg);
  root.style.setProperty('--form-control-bg', branding.colors.formControlBg);
  root.style.setProperty('--form-control-text', branding.colors.formControlText);
  root.style.setProperty('--avatar-outline-color', branding.avatarOutline.color);
  root.style.setProperty('--avatar-outline-width', `${branding.avatarOutline.width}px`);
  root.style.setProperty('--ai-message-outline-color', branding.aiMessageOutline.color);
  root.style.setProperty('--ai-message-outline-width', `${branding.aiMessageOutline.width}px`);
  root.style.setProperty('--user-message-outline-color', branding.userMessageOutline.color);
  root.style.setProperty('--user-message-outline-width', `${branding.userMessageOutline.width}px`);
  root.style.setProperty('--prompt-outline-color', branding.promptOutline.color);
  root.style.setProperty('--prompt-outline-width', `${branding.promptOutline.width}px`);
  root.style.setProperty('--image-outline-color', branding.imageOutline.color);
  root.style.setProperty('--image-outline-width', `${branding.imageOutline.width}px`);
  root.style.setProperty('--playback-pill-outline-color', branding.playbackPillOutline.color);
  root.style.setProperty('--playback-pill-outline-width', `${branding.playbackPillOutline.width}px`);
  root.style.setProperty('--checklist-trigger-outline-color', branding.checklistTriggerOutline.color);
  root.style.setProperty('--checklist-trigger-outline-width', `${branding.checklistTriggerOutline.width}px`);
}

export function BrandingProvider({
  selectedMachine,
  children,
}: {
  selectedMachine: string;
  children: React.ReactNode;
}) {
  const [manifest, setManifest] = useState<BrandingManifest>({});

  useEffect(() => {
    let cancelled = false;
    fetch(MANIFEST_URL)
      .then((r) => (r.ok ? r.json() : {}))
      .then((data) => {
        if (!cancelled) setManifest(data as BrandingManifest);
      })
      .catch((e) => logger.warn('Branding manifest not loaded:', e));
    return () => {
      cancelled = true;
    };
  }, []);

  const branding = useMemo(
    () => resolveBranding(findManifestEntry(manifest, selectedMachine)),
    [manifest, selectedMachine]
  );

  useEffect(() => {
    applyBrandingVars(branding);
  }, [branding]);

  return (
    <BrandingContext.Provider value={{ branding }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (ctx === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return ctx.branding;
}
